// Signed in without an account selected: an operator who isn't a member anywhere yet, or a person
// whose memberships are all gone. On a fresh installation this is the only screen the bootstrap
// operator can reach, so it covers the first steps: two-factor sign-in (operator rights need it on
// shared installations), then the first customer account.
import {
  api,
  initials,
  post,
  session,
  type TenantSummary,
  type TokenResponse,
  useTenantGate,
} from "@keel/core";
import { NewCustomer, TotpSetup } from "@keel/module-platform";
import { Empty, Icon } from "@keel/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

export function NoTenant() {
  const { me, logout, onSwitch } = useTenantGate();
  const [enrol, setEnrol] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [create, setCreate] = useState(false);
  const tenants = useQuery({
    queryKey: ["my-tenants"],
    queryFn: () => api<TenantSummary[]>("/auth/tenants"),
  });
  const enter = async (id: string) => {
    const t = await post<TokenResponse>("/auth/switch-tenant", { tenantId: id });
    session.token = t.token;
    session.company = null;
    onSwitch();
  };
  const operator = !!me?.operator;
  const mfaOn = !!me?.mfaEnabled || enrolled;
  return (
    <div className="login">
      <div className="login-pane">
        <div>
          <h1>Choose an account</h1>
          <p className="sub">
            {operator
              ? "You run this installation. Pick a customer account to work in, or create one."
              : "You aren't a member of any account yet."}
          </p>
        </div>
        <div className="stack" style={{ gap: 8 }}>
          {(tenants.data ?? []).map((t) => (
            <button
              key={t.id}
              type="button"
              className="btn"
              style={{ justifyContent: "flex-start", height: 44 }}
              onClick={() => enter(t.id)}
            >
              <span className="avatar">{initials(t.name)}</span>
              {t.name}
              <span className="muted mono" style={{ marginLeft: "auto", fontSize: 12 }}>
                {t.slug}
              </span>
            </button>
          ))}
          {operator && tenants.data?.length === 0 && (
            <Empty title="No customer accounts yet" text="Create the first one to start working." />
          )}
          {operator && (
            <button type="button" className="btn btn-primary" onClick={() => setCreate(true)}>
              <Icon name="plus" size={14} />
              New customer
            </button>
          )}
          {!operator && !mfaOn && (
            <div className="banner">
              Ask an account admin for an invitation. If you run this installation, turn on two-factor sign-in
              first: operator rights need it.
            </div>
          )}
          {!operator && !mfaOn && (
            <button type="button" className="btn btn-primary" onClick={() => setEnrol(true)}>
              <Icon name="key" size={14} />
              Set up two-factor sign-in
            </button>
          )}
          {!operator && mfaOn && (
            <div className="banner">
              {enrolled
                ? "Two-factor sign-in is on. Sign out and sign in again with a code from your app to get operator rights."
                : "Ask an account admin for an invitation. If you run this installation, sign out and sign in again with a code from your authenticator app."}
            </div>
          )}
        </div>
        <button type="button" className={`btn ${enrolled ? "btn-primary" : "btn-ghost"}`} onClick={logout}>
          Sign out
        </button>
      </div>
      {enrol && <TotpSetup onClose={() => setEnrol(false)} onDone={() => setEnrolled(true)} />}
      {create && <NewCustomer onClose={() => setCreate(false)} />}
    </div>
  );
}
